import BaseController from "./base_controller.js";
import { api, APIError, API_BASE_URL } from "/js/services/api.js";

const VAT_NUMBER_REGEX = /^FR\d{11}$/;
const MONTH_LABELS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

export default class VatIntracomRequestController extends BaseController {
  static targets = [
    "errorBanner", "errorMessage",
    "step1", "step2",
    "generateBtn", "downloadBtn",
    "generationDate",
    "thresholdDate", "triggerInvoice", "cumulativeRevenue", "sie", "siret",
    "numberInput", "numberError",
    "submitBtn", "successMsg",
  ];

  async connect() {
    this._isGenerating = false;
    this._isSubmitting = false;

    try {
      const status = await api.get("/me/vat-status");
      this._applyInitialState(status);
    } catch (error) {
      const message = error instanceof APIError
        ? error.message
        : "Impossible de charger l'état de votre numéro de TVA intracommunautaire.";
      this._showErrorBanner(message);
    }
  }

  _applyInitialState(status) {
    if (status.vatTaxable !== true) {
      this._showErrorBanner(
        "Votre entreprise n'est pas encore assujettie à la TVA. Cette demande sera disponible après franchissement du seuil majoré.",
      );
      this.step1Target.hidden = true;
      this.step2Target.hidden = true;
      return;
    }

    this._applyRecap(status);

    const intracomStatus = status.vatIntracomStatus ?? "not_requested";

    if (intracomStatus === "received") {
      this.step1Target.hidden = true;
      this.step2Target.hidden = false;
      if (status.vatIntracomNumber) {
        this.numberInputTarget.value = status.vatIntracomNumber;
      }
      this.numberInputTarget.disabled = true;
      this.submitBtnTarget.disabled = true;
      this.successMsgTarget.hidden = false;
      return;
    }

    if (intracomStatus === "requested") {
      this.step1Target.hidden = false;
      this.step2Target.hidden = false;
      this._setGenerationDateToday();
      return;
    }

    this.step1Target.hidden = false;
    this.step2Target.hidden = true;
  }

  async generate(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (this._isGenerating) return;

    this._isGenerating = true;
    this._setGenerateBusy(true);
    this._hideErrorBanner();

    try {
      const response = await fetch(`${API_BASE_URL}/me/vat-intracom/request`, {
        method: "POST",
        credentials: "include",
        headers: { "Accept": "application/pdf" },
      });

      if (!response.ok) {
        await this._handleFetchError(response);
        return;
      }

      const blob = await response.blob();
      this._triggerDownload(blob, response.headers.get("Content-Disposition"));
      this._setGenerationDateToday();
      this.step2Target.hidden = false;
    } catch {
      this._showErrorBanner("Le courrier SIE n'a pas pu être généré. Réessayez dans un instant.");
    } finally {
      this._isGenerating = false;
      this._setGenerateBusy(false);
    }
  }

  async download(event) {
    return this.generate(event);
  }

  validateInput() {
    const value = this.numberInputTarget.value.trim().toUpperCase();
    if (value === "") {
      this._clearNumberError();
      return;
    }
    if (!VAT_NUMBER_REGEX.test(value)) {
      this._showNumberError("Format invalide. Attendu : FR suivi de 11 chiffres.");
      return;
    }
    this._clearNumberError();
  }

  async submit(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (this._isSubmitting) return;

    const value = this.numberInputTarget.value.trim().toUpperCase();
    if (value === "") {
      this._showNumberError("Le numéro est requis.");
      this.numberInputTarget.focus();
      return;
    }
    if (!VAT_NUMBER_REGEX.test(value)) {
      this._showNumberError("Format invalide. Attendu : FR suivi de 11 chiffres.");
      this.numberInputTarget.focus();
      return;
    }

    this._isSubmitting = true;
    this.submitBtnTarget.disabled = true;
    this._hideErrorBanner();
    this._clearNumberError();
    this.successMsgTarget.hidden = true;

    try {
      await api.put("/me/vat-intracom/number", { number: value });
      this.numberInputTarget.value = value;
      this.numberInputTarget.disabled = true;
      this.successMsgTarget.textContent = "Numéro de TVA intracommunautaire enregistré.";
      this.successMsgTarget.hidden = false;
      this.step1Target.hidden = true;
    } catch (error) {
      this._handleSubmitError(error);
      this.submitBtnTarget.disabled = false;
    } finally {
      this._isSubmitting = false;
    }
  }

  async _handleFetchError(response) {
    if (response.status === 401) {
      window.location.assign("/connexion.html");
      return;
    }
    let message = `Erreur ${response.status}.`;
    try {
      const data = await response.json();
      if (data && data.error && typeof data.error.message === "string") {
        message = data.error.message;
      }
    } catch {
      // ignore JSON parse failures
    }
    if (response.status === 409) {
      message = "Le numéro de TVA intracommunautaire a déjà été enregistré.";
    } else if (response.status === 422) {
      message = message || "L'entreprise n'est pas encore assujettie à la TVA.";
    }
    this._showErrorBanner(message);
  }

  _handleSubmitError(error) {
    if (!(error instanceof APIError)) {
      this._showErrorBanner("Une erreur réseau est survenue. Réessayez.");
      return;
    }
    if (error.status === 422) {
      const fields = error.data?.error?.details?.fields ?? [];
      const numberField = fields.find((f) => f.field === "number");
      this._showNumberError(numberField?.message ?? error.message);
      return;
    }
    if (error.status === 409) {
      this._showErrorBanner("Ce numéro a déjà été enregistré.");
      return;
    }
    this._showErrorBanner(error.message);
  }

  _triggerDownload(blob, contentDisposition) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const match = (contentDisposition ?? "").match(/filename="([^"]+)"/);
    link.download = match ? match[1] : "courrier-sie-tva-intracom.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  _applyRecap(status) {
    const fallback = "—";
    if (this.hasTriggerInvoiceTarget) {
      this.triggerInvoiceTarget.textContent = status.triggerInvoiceLabel ?? fallback;
    }
    if (this.hasCumulativeRevenueTarget) {
      this.cumulativeRevenueTarget.textContent = status.cumulativeRevenueLabel ?? fallback;
    }
    if (this.hasSieTarget) {
      this.sieTarget.textContent = status.sieLabel ?? fallback;
    }
    if (this.hasSiretTarget) {
      this.siretTarget.textContent = status.siret ?? fallback;
    }
    if (this.hasThresholdDateTarget) {
      const label = status.thresholdDateLabel ?? fallback;
      this.thresholdDateTargets.forEach((el) => { el.textContent = label; });
    }
  }

  _setGenerationDateToday() {
    if (!this.hasGenerationDateTarget) return;
    const now = new Date();
    const label = `${now.getDate()} ${MONTH_LABELS_FR[now.getMonth()]} ${now.getFullYear()}`;
    this.generationDateTargets.forEach((el) => { el.textContent = label; });
  }

  _setGenerateBusy(busy) {
    this.generateBtnTarget.disabled = busy;
    this.generateBtnTarget.setAttribute("aria-busy", busy ? "true" : "false");
  }

  _showNumberError(message) {
    this.numberErrorTarget.textContent = message;
    this.numberErrorTarget.hidden = false;
    this.numberInputTarget.setAttribute("aria-invalid", "true");
  }

  _clearNumberError() {
    this.numberErrorTarget.textContent = "";
    this.numberErrorTarget.hidden = true;
    this.numberInputTarget.removeAttribute("aria-invalid");
  }

  _showErrorBanner(message) {
    if (!this.hasErrorBannerTarget) return;
    if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = message;
    this.errorBannerTarget.hidden = false;
  }

  _hideErrorBanner() {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.hidden = true;
  }
}
